import React from 'react';
import { PageHeader } from '../components/layout/PageHeader';
import { CtaBand } from '../components/ui/CtaBand';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Seal } from '../components/brand/Seal';
import { principles, testimonials } from '../data/site';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
const standards = [{
  title: 'Clinician-written protocols only',
  detail: 'Nothing ships from a checkout. Every prescription is written by a clinician licensed in your state after reading your panel.'
}, {
  title: 'US-licensed pharmacies, named',
  detail: 'Republic dispenses through US 503A and 503B pharmacies and retail wholesalers. The filling pharmacy is on your documentation.'
}, {
  title: 'Published pricing',
  detail: 'Membership and medication are priced separately and shown before approval. Your rate is held twelve months.'
}, {
  title: 'Portable records',
  detail: 'Labs, protocols and notes download as one PDF. Take them to any provider, any time, at no cost.'
}];
export function About() {
  useDocumentMeta({
    title: 'About Republic — A modern institution',
    description: 'Access, transparency, choice and control. Republic is a direct-pay clinical practice built to show its work.'
  });
  return <main data-id="element-276" data-mp="1yi28m99ah7nk">
      <PageHeader eyebrow="About Republic" title="A modern institution, not another storefront." intro="Republic was built on the belief that clinically serious care can also be transparent, fairly priced and genuinely accessible — and that patients should not have to choose between the three." data-id="element-277" data-mp="1ylhrzo9zx2us" />

      <section className="bg-cream" data-id="element-278" data-mp="2i7mizo50ynxp">
        <div className="mx-auto max-w-shell px-6 pb-20 pt-12 md:px-8 md:pb-28" data-id="element-279" data-mp="2i8nqjimu0sao">
          <div className="grid gap-14 lg:grid-cols-[1.1fr_0.9fr] lg:gap-20" data-id="element-280" data-mp="2w6cgh4vrapml">
            <div data-id="element-281" data-mp="2r8aw8gmlgk9j">
              <p className="font-serif text-3xl leading-[1.2] text-ink md:text-[2.5rem]" data-id="element-282" data-mp="2r8u4f5mfzuad">
                Most people do not lack motivation. They lack access, an honest price and someone who
                will read the whole panel.
              </p>
              <div className="mt-8 flex flex-col gap-5 text-base leading-relaxed text-ink-soft md:text-lg" data-id="element-283" data-mp="1stvk91tcdoiq">
                <p data-id="element-284" data-mp="22m1ao73y3xvt">
                  The last decade of digital health optimised for speed to checkout. It produced
                  clinics where the medication arrives faster than the explanation, where the price
                  changes with the dose, and where nobody can tell you which pharmacy filled the
                  vial.
                </p>
                <p data-id="element-285" data-mp="2gn3vyhn4945o">
                  Republic is organised the other way around. Data first, then a written protocol,
                  then a price you can see before you approve it. The clinical standard is
                  conventional; what is different is how much of it we show you.
                </p>
              </div>
            </div>
            <div className="flex items-start justify-center" data-id="element-286" data-mp="jnd5hdeyvdd8">
              <Seal className="h-56 w-56 text-ink/70" data-id="element-287" data-mp="joo8vc4i2j3t" />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-ink text-cream-light" data-id="element-288" data-mp="euk3uyesol5d">
        <div className="mx-auto max-w-shell px-6 py-20 md:px-8 md:py-28" data-id="element-289" data-mp="9yliljorjj96">
          <SectionHeading eyebrow="Principles" tone="cream" title="Four commitments, in order." data-id="element-290" data-mp="4yqnt51va9iu" />
          <ul className="mt-14 grid gap-10 md:grid-cols-2 lg:grid-cols-4 lg:gap-0 lg:divide-x lg:divide-cream/20" data-id="element-291" data-mp="12lza329ozmlw">
            {principles.map(principle => <li key={principle.name} className="lg:px-8 lg:first:pl-0 lg:last:pr-0" data-id="element-292" data-mp="xl1pkj17mgh8">
                <h3 className="font-serif text-3xl text-cream-light" data-id="element-293" data-mp="sm8bopi0gngj">{principle.name}</h3>
                <p className="mt-4 text-base leading-relaxed text-cream/70" data-id="element-294" data-mp="olwrtexh2r64">{principle.detail}</p>
              </li>)}
          </ul>
        </div>
      </section>

      <section className="bg-cream-light" data-id="element-295" data-mp="39cfekw8vf8ij">
        <div className="mx-auto max-w-shell px-6 py-20 md:px-8 md:py-28" data-id="element-296" data-mp="39erwj9empivl">
          <SectionHeading eyebrow="Clinical standards" title="What we hold ourselves to." data-id="element-297" data-mp="1137cy04d26qa" />
          <ul className="mt-12 grid gap-10 md:grid-cols-2 lg:gap-x-20" data-id="element-298" data-mp="15vh93xw2k310">
            {standards.map(standard => <li key={standard.title} className="rule pt-6" data-id="element-299" data-mp="r7dz8dewo76x">
                <h3 className="font-serif text-2xl leading-snug text-ink" data-id="element-300" data-mp="w44i18i3hgw1">{standard.title}</h3>
                <p className="mt-3 max-w-lg text-base leading-relaxed text-ink-soft" data-id="element-301" data-mp="1jtyptbnk4f9f">
                  {standard.detail}
                </p>
              </li>)}
          </ul>
        </div>
      </section>

      <section className="bg-cream-deep" data-id="element-302" data-mp="1asqxda7q28n9">
        <div className="mx-auto max-w-shell px-6 py-20 md:px-8 md:py-24" data-id="element-303" data-mp="1aw4xscwts7s2">
          <ul className="grid gap-12 md:grid-cols-2 md:gap-16" data-id="element-304" data-mp="1etswlr1n2kp8">
            {testimonials.map(testimonial => <li key={testimonial.name} data-id="element-305" data-mp="3vm44yk61qctm">
                <blockquote data-id="element-306" data-mp="3gqir3qxc98r">
                  <p className="font-serif text-2xl leading-[1.25] text-ink md:text-3xl" data-id="element-307" data-mp="3h0aowvj4qh3">
                    “{testimonial.quote}”
                  </p>
                  <footer className="rule mt-8 pt-5 text-sm text-ink-muted" data-id="element-308" data-mp="3n394kob1yddd">
                    <span className="text-ink" data-id="element-309" data-mp="3n5d6j0pe52b0">{testimonial.name}</span> · {testimonial.detail}
                  </footer>
                </blockquote>
              </li>)}
          </ul>
        </div>
      </section>

      <CtaBand data-id="element-310" data-mp="39bceepsy2djm" />
    </main>;
}